import { SectionHeading } from "@simplicity/ui";
import { useEffect, useState } from "react";
import { apiFetch } from "../../../api";

/**
 * Read-only effective-scope viewer for a PowerSchool connector — Slice D of the scoping framework
 * (SIM-986). SIM-1026: per-dimension gating, so it renders only the filter values the runner would
 * actually apply per table. Reference and inheritFrom tables take distinct paths, and a
 * non-applicable dimension renders an em-dash rather than the project value.
 */

type DisabledKey = "schoolids" | "sinceDate";
type TableClass = "scoped-direct" | "inherited" | "reference" | "unknown";

interface SchoolidsScope {
  value: string[];
  raw: string[];
}

interface SinceDateScope {
  value: string | null;
  raw: string | null;
}

interface InheritedFrom {
  parent: string;
  joinColumn: string;
  /**
   * Slice F.2 (SIM-1042): when the child references a non-PK column on the
   * parent (e.g. `incident.School_Number` → `schools.school_number`), the
   * handler surfaces it here so the viewer can render the full join shape.
   */
  parentColumn?: string;
}

interface CadenceScope {
  cadence: string;
  tableClass: TableClass;
  inheritedFrom?: InheritedFrom;
  schoolids?: SchoolidsScope;
  sinceDate?: SinceDateScope;
  studentScope?: { mode: "in-list-chunked" };
  unfiltered: boolean;
  disabled: DisabledKey[];
  scopeSource: "knowledge" | "heuristic" | "none";
}

interface TableScope {
  name: string;
  tableClass: TableClass;
  inheritedFrom?: InheritedFrom;
  cadences: CadenceScope[];
}

interface EffectiveScopeResponse {
  connectorId: string;
  connectorType: "powerschools" | "other";
  tables: TableScope[];
}

interface EffectiveScopePanelProps {
  connectorId: string;
}

function EmDash() {
  return <span className="text-muted-foreground">—</span>;
}

function SchoolidsLine({ scope }: { scope: SchoolidsScope }) {
  return (
    <div className="font-mono text-xs">
      schoolids: {scope.value.length > 0 ? `[${scope.value.join(",")}]` : <EmDash />}
    </div>
  );
}

function SinceDateLine({ scope }: { scope: SinceDateScope }) {
  if (scope.value === null) {
    return (
      <div className="font-mono text-xs">
        sinceDate: <EmDash />
      </div>
    );
  }
  const showRaw = scope.raw && scope.raw !== scope.value;
  return (
    <div className="font-mono text-xs">
      sinceDate: {scope.value}
      {showRaw && <span className="text-muted-foreground"> ({scope.raw})</span>}
    </div>
  );
}

function ScopedDirectCell({ scope }: { scope: CadenceScope }) {
  if (scope.unfiltered) {
    return <span className="text-muted-foreground italic">unfiltered</span>;
  }
  if (scope.disabled.length > 0) {
    return (
      <span className="text-muted-foreground italic">disabled: {scope.disabled.join(",")}</span>
    );
  }

  const lines: React.ReactNode[] = [];
  if (scope.schoolids) lines.push(<SchoolidsLine key="schoolids" scope={scope.schoolids} />);
  if (scope.sinceDate) lines.push(<SinceDateLine key="sinceDate" scope={scope.sinceDate} />);
  if (scope.studentScope) {
    lines.push(
      <div key="studentScope" className="text-muted-foreground font-mono text-xs">
        scoped via student-IN (chunked)
      </div>,
    );
  }

  if (lines.length === 0) {
    return <span className="text-muted-foreground">(none)</span>;
  }
  return <div className="space-y-1">{lines}</div>;
}

function renderCell(scope: CadenceScope, tableClass: TableClass): React.ReactNode {
  if (tableClass === "reference") {
    return (
      <span className="text-muted-foreground italic">
        reference table (district-wide, no scoping)
      </span>
    );
  }
  if (tableClass === "inherited" && scope.inheritedFrom) {
    // Slice F.2 (SIM-1042): when parentColumn is set, render the full join
    // shape — "scoped via <parent>.<parentColumn> → <joinColumn>". When omitted
    // (original Slice F PK-based cascade), keep the simpler form.
    const { parent, joinColumn, parentColumn } = scope.inheritedFrom;
    return (
      <span className="text-muted-foreground italic font-mono text-xs">
        scoped via{" "}
        {parentColumn ? (
          <>
            {parent}.{parentColumn} → {joinColumn}
          </>
        ) : (
          <>
            {parent}.{joinColumn}
          </>
        )}
      </span>
    );
  }
  if (tableClass === "unknown") {
    return <span className="text-muted-foreground italic">no scoping (unknown table)</span>;
  }
  return <ScopedDirectCell scope={scope} />;
}

export function EffectiveScopePanel({ connectorId }: EffectiveScopePanelProps) {
  const [data, setData] = useState<EffectiveScopeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<EffectiveScopeResponse>(`/api/admin/effective-scope/${connectorId}`)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setError(null);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load scope");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [connectorId]);

  if (loading) {
    return (
      <div className="form-card mb-6" data-testid="effective-scope-panel">
        <SectionHeading size="lg" as="h2">
          Effective scope (read-only)
        </SectionHeading>
        <div className="text-muted-foreground text-sm">Loading scope…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="form-card mb-6" data-testid="effective-scope-panel">
        <SectionHeading size="lg" as="h2">
          Effective scope (read-only)
        </SectionHeading>
        <div className="text-destructive text-sm">{error}</div>
      </div>
    );
  }

  if (data?.connectorType !== "powerschools" || data.tables.length === 0) {
    return null;
  }

  const cadences = data.tables[0]?.cadences?.map((c) => c.cadence) ?? ["default"];

  return (
    <div className="form-card mb-6" data-testid="effective-scope-panel">
      <div className="mb-2">
        <SectionHeading size="lg" as="h2">
          Effective scope (read-only)
        </SectionHeading>
      </div>
      <p className="text-muted-foreground mb-4 text-sm">
        Shown values are what the runner would use for a run starting now. Per-dimension filters
        appear only for tables where the column exists in the knowledge file.
      </p>
      <div className="overflow-x-auto">
        <table className="admin-table">
          <thead>
            <tr>
              <th className="text-left">Table</th>
              <th className="text-left">Source</th>
              {cadences.map((c) => (
                <th key={c} className="text-left">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.tables.map((table) => (
              <tr key={table.name}>
                <td className="font-mono text-xs">{table.name}</td>
                <td className="text-muted-foreground text-xs">
                  {table.cadences[0]?.scopeSource ?? "none"}
                </td>
                {cadences.map((cadenceName) => {
                  const scope = table.cadences.find((c) => c.cadence === cadenceName);
                  return (
                    <td key={cadenceName} className="align-top">
                      {scope ? renderCell(scope, table.tableClass) : <EmDash />}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
